<template>
  <ProvideSearch
    v-slot="{ sorted }"
    :records="entries"
    :keys="searchKeys"
    :search-pattern="searchInput"
    :disabled="inApiMode"
  >
    <!--
     Triggered on change to search or sorting has occurred
     @event input
     @property {Array} records - The set of active records currently displayed in the table
   -->
    <ProvidePagination
      v-slot="{ activePage, changePage, changePerPage, perPage, totalItems, records }"
      :records="sorted"
      :per-page="pageLength"
      :paginator-info="paginatorInfo"
      @page-update="handleUpdatePage"
      @per-page-update="handleUpdatePageLength"
      @change="$emit('input', $event)"
    >
      <ProvideLazyRendering
        v-slot="{ lazyRecords }"
        :records="records"
        :root="$refs.tbody"
        :trigger="$refs.trigger"
      >
        <div v-bind="$attrs" class="table-responsive" :class="{ 'tw-h-full': !slim }">
          <!-- @slot To replace the control bar entirely -->
          <slot
            name="control-bar"
            :update-search="(value) => (searchInput = value)"
            :search="searchInput"
            :selected-count="selectedCount"
            :page-length="pageLength"
            :page-length-options="pageLengthOptions"
          >
            <BaseDataTableControlBar
              v-if="!slim"
              :can-select-all="multiSelect"
              :selected-count="selectedCount"
              :total-count="entries.length"
              :page-length="pageLength"
              :page-length-options="pageLengthOptions"
              :search-disabled="searchDisabled"
              :select-all-disabled="!entries.length"
              :search-placeholder="searchPlaceholder"
              @toggle-all="toggleAll(records)"
              @search="handleSearch"
            >
              <template #left-aligned>
                <!-- @slot To add items inside the control bar, for example a bulk actions button -->
                <slot name="additional-control-bar-elements" />
              </template>
              <template #right-aligned>
                <!-- @slot To add items inside the control bar to the right, for example a custom search -->
                <slot name="additional-control-bar-elements-right" />
              </template>
            </BaseDataTableControlBar>
          </slot>
          <div
            class="tw-relative"
            :style="!slim ? `height: calc(${maxHeight} - 68px);` : ''"
            :class="{ stickyShadow }"
          >
            <div v-if="loading" class="loading-overlay" />
            <div
              ref="tbody"
              class="tw-relative tw-max-w-full tw-h-full tw-max-h-full table-wrap m-t-10 tw-min-w-full tw-inline-block"
              :class="{ 'tw-h-[100vh]': !slim }"
              @dragover="onDragOver(null, $event)"
              @drop="onDrop($event)"
            >
              <div class="tw-min-w-full tw-w-max tw-min-h-full tw-h-max">
                <table ref="table" class="table" style="width: 100%">
                  <!-- @slot To replace content inside `<table>` -->
                  <slot name="table-content">
                    <thead>
                      <!-- @slot To replace content inside `<thead>` -->
                      <slot name="thead" :headers="headers">
                        <!-- @TODO HANDLE SORTING ON THIS ^ SLOT -->
                        <TransitionGroup
                          tag="tr"
                          name="cols-header"
                          :class="['cols-header', { 'cols-header-flip-locked': draggingKey === null }]"
                        >
                          <!-- @slot To replace content inside `<thead><tr>` -->
                          <slot name="thead-tr">
                            <th
                              v-for="col of headers"
                              :key="col.key"
                              :draggable="col.draggable ?? false"
                              :class="{
                                ...col.colClass,
                                'sort-enabled': col.orderAble,
                                [ordering[col.key]]: ordering[col.key],
                                'sticky-enabled': col.sticky,
                                'tw-pointer': col.multiSelect && draggingKey === null,
                                'tw-cursor-grab': col.draggable && draggingKey === null,
                                'tw-cursor-grabbing': col.draggable && draggingKey !== null,
                                'column-dragging': draggingKey === col.key,
                                'locked-animation': loading,
                              }"
                              :data-test="`column-${col.key}`"
                              @click="handleOrder(col)"
                              @mousedown="prepareDragPreview(col, $event)"
                              @mouseup="cleanupDragPreview"
                              @dragover="onDragOver(col, $event)"
                              @dragstart="onDragStart(col, $event)"
                              @dragend="onDragEnd(col)"
                              @drop="onDrop($event)"
                              @drag="$emit('column-drag', col)"
                              @dragenter="$emit('column-dragenter', col)"
                              @dragleave="$emit('column-dragleave', col)"
                            >
                              <div
                                :class="{
                                  'tw-flex': true,
                                  'tw-items-center': true,
                                  'tw-text-nowrap': true,
                                  ...col.flexClass,
                                }"
                              >
                                <component
                                  :is="col.renderHeadUsing.component"
                                  v-if="col.renderHeadUsing && col.renderHeadUsing.component"
                                />
                                <slot v-else :name="'Head-' + col.slot" :col="col">
                                  {{ $t(col.title || '') }}
                                </slot>
                                <div v-if="col.multiselect" @click="toggleAll(records)">
                                  <WdsCheckbox
                                    :indeterminate="
                                      !loading &&
                                      selectedCount > 0 &&
                                      selectedCount < records.length
                                    "
                                    :checked="
                                      !loading &&
                                      records.length > 0 &&
                                      selectedCount === records.length
                                    "
                                    @click.prevent
                                  />
                                </div>
                                <div class="tw-flex-shrink-1 tw-basis-2"></div>
                                <template v-if="col.orderAble">
                                  <WdsIcon
                                    v-if="!ordering[col.key]"
                                    class="sort-icon tw-flex-shrink-0"
                                    name="chevron-sort"
                                    :size="16"
                                  />
                                  <WdsIcon
                                    v-else
                                    class="sort-icon tw-flex-shrink-0"
                                    name="chevron-up"
                                    :size="16"
                                  />
                                </template>
                              </div>
                            </th>
                          </slot>
                        </TransitionGroup>
                      </slot>
                    </thead>
                    <tbody>
                      <!-- @slot To replace content inside `<tbody>` -->
                      <slot name="tbody">
                        <tr
                          v-if="lazyRecords.length === 0"
                          class="text-center table-empty"
                          role="row"
                        >
                          <td
                            :colspan="headers.length"
                            :data-test="
                              loading
                                ? 'base-data-table-loading-indicator'
                                : 'base-data-table-no-results-indicator'
                            "
                          >
                            <!-- @slot For 'no results found' content -->
                            <slot name="no-results" :loading="loading" :search="searchInput">
                              {{ loading ? $t('Loading') : $t('No matching records found') }}
                            </slot>
                          </td>
                        </tr>
                        <BaseDataTableRowLazy
                          v-for="({ render, row }, ri) of lazyRecords"
                          :key="getKey(row, ri)"
                          :initial-render="render"
                          :class="getRowClass(ri, lazyRecords)"
                          :tall="tallRows"
                        >
                          <!-- @slot To replace content inside `<tbody><tr>` <br>@bindings rowData, rowKey -->
                          <slot name="tbody-tr" :row-data="row" :row-key="ri">
                            <td
                              v-for="(col, ci) of headers"
                              :key="getKey(row, col.key + ci + ri)"
                              :class="[
                                col.class ? col.class : '',
                                col.sticky ? 'sticky-enabled' : '',
                                draggingKey === col.key ? 'column-dragging' : '',
                              ]"
                              @click="!col.ignoreClickForRowSelection && rowSelect(row)"
                              @dragover="onDragOver(col, $event)"
                              @drop="onDrop($event)"
                            >
                              <!-- Use a specific component if one was provided in the columns config -->
                              <template v-if="col.renderUsing && col.renderUsing.component">
                                <component
                                  :is="col.renderUsing.component"
                                  v-if="(col.renderUsing.shouldRender ?? ((row) => true))(row)"
                                  v-bind="col.renderUsing?.componentProps?.(row, ri)"
                                />
                                <span v-else>-</span>
                              </template>
                              <!-- If no component was specified, use the named slot with raw data as a fallback -->
                              <slot
                                v-else
                                :col="col"
                                :row="row"
                                :row-index="ri"
                                :name="col.slot ? col.slot : col.title"
                                :is-row-selected="!!selectedRows[getKey(row)]"
                              >
                                <template v-if="col.slot === 'Select'">
                                  <WdsCheckbox
                                    :checked="!!selectedRows[getKey(row)]"
                                    @click.prevent
                                  />
                                </template>
                                <!-- If no slot template was provided, render the raw data -->
                                <template v-else>
                                  {{
                                    row[col.key] !== null && row[col.key] !== undefined
                                      ? row[col.key]
                                      : ''
                                  }}
                                </template>
                              </slot>
                            </td>
                          </slot>
                        </BaseDataTableRowLazy>
                      </slot>
                    </tbody>
                  </slot>
                </table>
              </div>
              <div ref="trigger"></div>
            </div>
          </div>
          <WdsPagination
            v-if="!slim"
            :page="activePage - 1"
            :number-of-items="totalItems"
            :items-per-page="perPage"
            :page-length-options="paginationDropdownOptions"
            class="tw-py-4 tw-px-4"
            @update:page="changePage"
            @update:per-page="changePerPage"
          />
        </div>
      </ProvideLazyRendering>
    </ProvidePagination>
  </ProvideSearch>
</template>

<script>
import { WdsPagination, WdsCheckbox, WdsIcon } from '@worksome/wds-components'
import { useScroll } from '@vueuse/core'
import { computed, onBeforeUnmount, onUpdated, ref, toRefs, useTemplateRef, watch } from 'vue'
import HashMixin from '@/mixins/MethodHash'
import BaseDataTableControlBar from '@/components/BaseDataTable/BaseDataTableControlBar.vue'
import ProvidePagination from '@/components/ProvidePagination.vue'
import ProvideSearch from '@/components/ProvideSearch.vue'
import BaseDataTableRowLazy from '@/components/BaseDataTable/BaseDataTableRowLazy.vue'
import ProvideLazyRendering from '@/components/ProvideLazyRendering.vue'
import { graphqlFragmentValidator } from '@/utils/propValidation'
import { textHelpers, debounce } from '@/utils/tools'

export default {
  name: 'BaseDataTable',
  components: {
    WdsPagination,
    WdsIcon,
    WdsCheckbox,
    BaseDataTableControlBar,
    ProvidePagination,
    ProvideSearch,
    BaseDataTableRowLazy,
    ProvideLazyRendering,
  },
  mixins: [HashMixin],
  inheritAttrs: false,
  fragments: {
    pagination: ProvidePagination.fragments.pagination,
  },
  props: {
    /**
     * Array of row data objects which all have the same keys
     */
    entries: {
      type: Array,
      default: () => [],
    },
    /**
     * Definitions of each column, and the access points for each column in the rows
     * Each column needs to contain at least a title prop, and a key prop ie: {key: 'foo', title: 'bar'}
     * Other options are
     * {
     *   class | row cell class
     *   colClass | column header class
     *   flexClass | column flex classes. Allows to control the classes on the headers flex div
     *   order | the value used to order a column, can be a function(row) but is primarily a key for the row object
     *   slot | name of the slot, if no slot is added title is used instead,
     *          if added, can also access the column slot at Head-${slot}
     *   orderAble | makes the column abel to be ordered in asc or desc
     *   sort | the path to the value to sort and search the column by ie sort.by.this.value if the row object
     *          is more deep. If not provided the key will be used instead.
     *   searchKeys | A list of search keys for fuse.js to use. If not provided, the key will be used instead
     *   hide | if true, the column won't be rendered
     *   sticky | "sticks" the column so that it always visible on the table. Best used for action menus that can be
     *            hidden by overflow on smaller screens
     * }
     */
    columns: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Ability to select multiple rows
     * For selected rows listen on the @rows-selected event
     */
    multiSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * Pre-selected rows (when using multi-select)
     */
    preSelectedRows: {
      type: Array,
      default: () => [],
    },
    /**
     * Selected rows object for v-model binding
     * Format: { [rowKey]: rowData }
     */
    modelValue: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Results from lighthouses @pagination directive.
     * If given and validated this switches the datatable into gql mode where inputs are not applied to the entries
     * but only emitted for the implementation component to handle sorting pagination and so forth
     */
    paginatorInfo: {
      type: Object,
      default: undefined,
      validator: graphqlFragmentValidator(ProvidePagination.fragments.pagination),
    },
    /**
     * Multiselect will not be triggered by clicking on the last td on each row
     * Useful for things like dropdown menus & action buttons in the last column
     */
    preventRowSelectionOnLastTd: {
      type: Boolean,
      default: false,
    },
    /**
     * Dont use search, pagination, etc
     */
    slim: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines which key to use when building the array for @selection-updated event for multi-select
     * fx setting this to 'name', would emit ["Danny", "Spaghetti Sam"]
     */
    multiSelectTargetKey: {
      type: String,
      default: 'globalId',
    },
    defaultPageLength: {
      type: Number,
      default: 50,
    },
    defaultPageLengthOptions: {
      type: Array,
      default: () => [10, 50, 100],
    },
    searchDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Search placeholder text
     */
    searchPlaceholder: {
      type: String,
      default: $t('Search'),
    },
    /**
     * Determines whether or not to preserve the rows that are selected when the entries prop changes
     * ex. when filtering a table and a new entries reference is created but the selected rows are still relevant
     */
    preserveSelectedRows: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether to use default table row height or taller table row height.
     */
    tallRows: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether to apply a hover effect when hovering rows.
     */
    rowHover: {
      type: Boolean,
      default: false,
    },

    forceSearchInApiMode: {
      type: Boolean,
      default: false,
    },

    /**
     * This points a prop on **ALL** rows that uniquely identifies the row. Can massively boost performance as it skips
     * hashing and string encoding to generate keys.
     */
    rowUuid: {
      type: String,
      default: null,
    },

    maxHeight: {
      type: String,
      default: '70vh',
    },
  },
  emits: [
    'search',
    'input',
    'rows-selected',
    'selection-updated',
    'row-click',
    'row-selection-toggled',
    'update:modelValue',
    'select-all',
    'deselect-all',
    'column-dragover',
    'column-dragstart',
    'column-dragend',
    'column-drag',
    'column-dragenter',
    'column-dragleave',
  ],
  setup(props, ctx) {
    const { loading } = toRefs(props)
    const tbody = useTemplateRef('tbody')
    const table = useTemplateRef('table')
    const { arrivedState, isScrolling } = useScroll(tbody)
    const { right } = toRefs(arrivedState)

    // This part checks handles the logic where we load in elements for the first time before any content has been added
    // the right scroll value does not get updated, so we do a manual check after each tune the content is updated
    // lastly we only want this to have effect once since the right value handles it afterwards.
    const hasScrolled = ref(false)
    const hasScrollBars = ref(false)
    watch(
      isScrolling,
      () => {
        hasScrolled.value = true
      },
      { once: true },
    )

    // The header FLIP is for column dragging only; loading → loaded and other late list updates must not animate.
    const draggingKey = ref(null)
    const previewCache = new Map()

    function cleanupDragPreview() {
      previewCache.forEach((el) => el?.remove())
      previewCache.clear()
    }

    onBeforeUnmount(cleanupDragPreview)

    function getVueScopeAttr(el) {
      return el?.getAttributeNames?.().find((n) => n.startsWith('data-v-')) || null
    }

    function applyScopeAttrDeep(rootEl, scopeAttr) {
      if (!rootEl || !scopeAttr) return
      const stack = [rootEl]
      while (stack.length) {
        const el = stack.pop()
        el?.setAttribute?.(scopeAttr, '')
        if (el?.children) stack.push(...el.children)
      }
    }

    function neutralizeStickyInPreview(previewRoot) {
      previewRoot.querySelectorAll('th, td').forEach((el) => {
        Object.assign(el.style, {
          position: 'static',
          top: 'auto',
          right: 'auto',
          left: 'auto',
          zIndex: 'auto',
        })
      })
    }

    function syncHeights(thOriginal, thClone, tdsOriginal, tdClones) {
      // eslint-disable-next-line no-param-reassign
      thClone.style.height = `${Math.ceil(thOriginal.getBoundingClientRect().height)}px`
      tdsOriginal.forEach((td, i) => {
        if (tdClones[i])
          // eslint-disable-next-line no-param-reassign
          tdClones[i].style.height = `${Math.ceil(td.getBoundingClientRect().height)}px`
      })
    }

    function syncImages(srcRoot, dstRoot) {
      const srcImgs = srcRoot.querySelectorAll('img')
      const dstImgs = dstRoot.querySelectorAll('img')
      srcImgs.forEach((srcImg, i) => {
        const dstImg = dstImgs[i]
        if (!dstImg) return
        dstImg.src = srcImg.currentSrc || srcImg.src
        dstImg.removeAttribute('srcset')
        dstImg.removeAttribute('sizes')
        Object.assign(dstImg, { loading: 'eager', decoding: 'sync' })
        if (srcImg.width) dstImg.width = srcImg.width
        if (srcImg.height) dstImg.height = srcImg.height
      })
    }

    function isOutsideOverflowBounds(el) {
      if (!tbody.value) return true
      const p = tbody.value.getBoundingClientRect()
      const c = el.getBoundingClientRect()
      return c.left < p.left || c.right > p.right || c.top < p.top || c.bottom > p.bottom
    }

    function buildPreviewForColumn(col, evt) {
      const headCol = evt.currentTarget?.closest?.('th') ?? evt.currentTarget
      const idx = props.columns.findIndex((c) => c.key === col.key)
      if (idx === -1 || !table.value || !headCol) return null

      const nth = (props.multiSelect ? 1 : 0) + idx + 1
      const realTable = headCol.closest('table')
      if (!realTable) return null

      const rowsCols = Array.from(realTable.querySelectorAll(`tbody tr td:nth-child(${nth})`))
      const colWidth = Math.ceil(headCol.getBoundingClientRect().width)

      const preview = document.createElement('div')
      preview.className = 'dt-drag-preview'
      Object.assign(preview.style, {
        position: 'fixed',
        left: '-10000px',
        top: '-10000px',
        pointerEvents: 'none',
        zIndex: '999999',
        background: 'white',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      })

      const miniTable = document.createElement('table')
      miniTable.className = realTable.className
      Object.assign(miniTable.style, {
        borderCollapse: 'collapse',
        background: 'white',
        width: `${colWidth}px`,
      })

      const thead = document.createElement('thead')
      const trh = document.createElement('tr')
      const thClone = headCol.cloneNode(true)
      thClone.style.width = `${colWidth}px`
      trh.appendChild(thClone)
      thead.appendChild(trh)

      const tbodyEl = document.createElement('tbody')
      const tdClones = []

      for (const td of rowsCols) {
        if (!isOutsideOverflowBounds(td)) {
          const tr = document.createElement('tr')
          tr.className = td.parentElement?.className || ''
          const tdClone = td.cloneNode(true)
          tr.appendChild(tdClone)
          tbodyEl.appendChild(tr)
          tdClones.push(tdClone)
          syncImages(td, tdClone)
        }
      }

      miniTable.append(thead, tbodyEl)
      preview.appendChild(miniTable)
      document.body.appendChild(preview)

      applyScopeAttrDeep(preview, getVueScopeAttr(headCol))
      neutralizeStickyInPreview(preview)
      syncHeights(headCol, thClone, rowsCols, tdClones)

      return preview
    }

    function prepareDragPreview(col, evt) {
      if (previewCache.has(col.key)) return
      const preview = buildPreviewForColumn(col, evt)
      if (preview) previewCache.set(col.key, preview)
    }

    function onDragStart(col, evt) {
      draggingKey.value = col.key

      let preview = previewCache.get(col.key)
      if (!preview) {
        preview = buildPreviewForColumn(col, evt)
        if (preview) previewCache.set(col.key, preview)
      }

      if (preview) {
        Object.assign(preview.style, { left: '-10000px', top: '-10000px' })
        evt.dataTransfer.setDragImage(preview, 20, 20)
      } else {
        evt.dataTransfer.setDragImage(evt.target, 0, 0)
      }

      // eslint-disable-next-line no-param-reassign
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.setData('text/plain', col.key)
      ctx.emit('column-dragstart', col)
    }

    function onDragEnd(col) {
      draggingKey.value = null
      cleanupDragPreview()
      ctx.emit('column-dragend', col)
    }

    function onDragOver(col, evt) {
      if (draggingKey.value) {
        evt.preventDefault()
        // eslint-disable-next-line no-param-reassign
        evt.dataTransfer.dropEffect = 'move'
      }
      if (col) ctx.emit('column-dragover', col)
    }

    function onDrop(evt) {
      evt.preventDefault()
      draggingKey.value = null
    }

    onUpdated(() => {
      hasScrollBars.value = tbody.value?.scrollWidth > tbody.value?.offsetWidth
    })

    const stickyShadow = computed(
      () => (!loading.value && !right?.value) || (hasScrollBars.value && !hasScrolled.value),
    )

    return {
      stickyShadow,
      draggingKey,
      onDragEnd,
      onDragStart,
      onDragOver,
      prepareDragPreview,
      cleanupDragPreview,
      onDrop,
    }
  },
  data() {
    // This loop ensures that vue is properly reactive to changes to the preselected rows that we might have
    const selectedRows = Object.keys(this.modelValue).length > 0 ? { ...this.modelValue } : {}

    if (Object.keys(selectedRows).length === 0) {
      for (const row of this.preSelectedRows) {
        selectedRows[this.getKey(row)] = row
      }
    }

    return {
      searchInput: '',
      pageLength: this.defaultPageLength,
      pageLengthOptions: this.defaultPageLengthOptions,
      page: 0,
      height: visualViewport.height - 350,
      // Is formatted as { [col.key]: col.order }
      ordering: Object.assign({}, ...this.columns.map((col) => ({ [col.key]: col.order }))),
      selectedRows,
      inApiMode: false,
    }
  },
  computed: {
    /**
     * Headers will be the same as the columns prop
     * or interpreted from the first element in the :entries prop
     * @returns {*[]|*[]}
     */
    headers() {
      let headers
      if (this.columns.length) {
        headers = this.columns
      } else {
        headers = this.entries.length
          ? Object.keys(this.entries?.[0]).map((key) => ({
              key,
              title: textHelpers.keyToHumanReadable(key),
              orderAble: true,
            }))
          : null
      }
      // disable hidden items
      headers = headers?.filter((x) => !x._col?.hide) ?? []
      return this.multiSelect
        ? [{ slot: 'Select', title: '', multiselect: true, orderAble: false }, ...headers]
        : headers
    },
    searchKeys() {
      let keys = []
      for (const c of this.headers.filter((col) => col.orderAble !== false)) {
        if ((c.searchKeys ?? []).length !== 0) {
          keys = [
            ...keys,
            ...c.searchKeys.map((sk) => {
              const searchKey = { name: sk }
              return {
                name: searchKey.name,
                sort: this.ordering[sk.sort ?? searchKey.name],
                convert: sk.convert,
                getFn: c?._col?.getFn,
                type: sk.type,
              }
            }),
          ]
        } else {
          keys.push({
            name: c.sort ?? c.key,
            sort: this.ordering[c.key],
            convert: c.convert,
            getFn: c?._col?.getFn,
            type: c.type,
          })
        }
      }

      return keys
    },
    selectedCount() {
      return Object.values(this.selectedRows).length
    },
    paginationDropdownOptions() {
      return this.pageLengthOptions.map((value) => ({ value, label: String(value) }))
    },
    searchSettings: {
      get() {
        return {
          page: this.page,
          perPage: this.pageLength,
          search: this.searchInput,
          order: this.ordering,
        }
      },
      set({ page, perPage, search, order }) {
        this.page = page ?? this.page ?? 0
        this.pageLength = perPage ?? this.pageLength ?? 50
        this.searchInput = search ?? this.searchInput ?? ''

        function nextOrderDirection(dir) {
          switch (dir) {
            case undefined:
              return 'asc'
            case 'asc':
              return 'desc'
            default:
              return 'asc'
          }
        }
        // Might want to figure out a way where ordering takes priority somehow,
        // but not sure how or if the backend supports that
        if (order) {
          const keys = []
          for (const o of order) {
            this.ordering[o.key] = nextOrderDirection(this.ordering[o.key])
            keys.push(o.key)
          }
          // This part removes any currently selected sorting options that are not specified
          for (const key in this.ordering) {
            if (!keys.includes(key)) this.ordering[key] = undefined
          }
        }
        /**
         * Whenever the search settings changes this event gets fired
         * These changes can be search in the search bar, ordering or pagination changes.
         * @event search
         * @property {Object} searchSettings - the current active settings describing the search state of the table
         */
        this.$emit('search', this.searchSettings)
      },
    },
    shouldHoverRow() {
      return this.rowHover ? this.rowHover : this.multiSelect
    },
  },
  watch: {
    // Debounce the search if we are in api mode
    // Cant do in created() because this.paginatorInfo is not initially filled
    paginatorInfo(newValue, oldValue) {
      // Ensure code is only run once when paginatorInfo received
      if (!oldValue && newValue && !this.inApiMode) {
        // Since we received paginatorInfo, we are using api mode
        this.inApiMode = !this.forceSearchInApiMode
        this.handleSearch = debounce(this.handleSearch, 600)
      }
    },
    columns: {
      deep: true,
      handler() {
        this.ordering = Object.assign({}, ...this.columns.map((col) => ({ [col.key]: col.order })))
      },
    },

    entries: {
      deep: true,
      handler() {
        if (!this.preserveSelectedRows) {
          this.selectedRows = {}
          this.$emit('update:modelValue', {})
        }
      },
    },
    preSelectedRows(rows) {
      // Only update from preSelectedRows if modelValue is empty
      if (Object.keys(this.modelValue).length === 0) {
        for (const row of rows) {
          this.selectedRows[this.getKey(row)] = row
        }
        this.$emit('update:modelValue', { ...this.selectedRows })
      }
    },
    modelValue: {
      deep: true,
      handler(newValue) {
        this.selectedRows = { ...newValue }
      },
    },
  },
  created() {
    window.addEventListener('resize', () => {
      this.height = visualViewport.height - 350
    })
  },
  methods: {
    toggleAll(data) {
      // We check if all of the rows are already in the selected rows, in which case we remove them to clear
      // the paginated/filtered rows
      if (data.every((row) => !!this.selectedRows[this.getKey(row)])) {
        // Remove them all as all are selected
        for (const row of data) {
          delete this.selectedRows[this.getKey(row)]
        }
        this.$emit('deselect-all')
        // Works like Vue.set but is manually triggered here as Vue.set does not handle delete operations
        this.emitUpdates()
        return
      }

      for (const row of data) {
        this.selectedRows[this.getKey(row)] = row
      }
      this.$emit('select-all')
      this.emitUpdates()
    },
    getKey(row, index = null) {
      return `${this.rowUuid ? row?.[this.rowUuid] : this.hash(row)}${index ? `-${index}` : ''}`
    },
    handleSearch(search) {
      // Do search n stuff
      this.searchSettings = { search }
    },
    handleUpdatePageLength(perPage) {
      this.searchSettings = { perPage }
    },
    handleUpdatePage(page) {
      this.searchSettings = { page }
    },
    handleOrder(col) {
      if (col.orderAble) {
        this.searchSettings = { order: [col] }
      }
    },
    emitUpdates() {
      /**
       * Anytime changes occur to the rows
       *
       * @event rows-selected
       * @property {Array} selectedRows - The list of current rows to have been selected
       */
      this.$emit('rows-selected', this.selectedRows)
      /**
       * Anytime changes occur to the rows
       *
       * @event selection-updated
       * @property {Array} selectedRowIDs - Array of the current selected row global ids
       */
      this.$emit(
        'selection-updated',
        Object.values(this.selectedRows).map(
          (dataPoint) => dataPoint[this.multiSelectTargetKey] || '',
        ),
      )
      /**
       * Update the v-model value
       *
       * @event update:modelValue
       * @property {Object} selectedRows - The current selected rows object
       */
      this.$emit('update:modelValue', { ...this.selectedRows })
    },
    rowSelect(row) {
      /**
       * @event row-click
       * @property {Object} row - The row that has been clicked
       */
      this.$emit('row-click', row)
      if (this.multiSelect) {
        const hash = this.getKey(row)
        if (this.selectedRows[hash] !== undefined) {
          delete this.selectedRows[hash]
          this.$emit('row-selection-toggled', row, false)
        } else {
          this.selectedRows[hash] = row
          this.$emit('row-selection-toggled', row, true)
        }
      }
      this.emitUpdates()
    },
    getRowClass(rowIndex, records) {
      const rowClass = this.shouldHoverRow ? 'row-hover' : ''

      if (this.selectedRows[this.getKey(records[rowIndex]?.row)]) {
        // Checks the next in line row to see if it has been selected, if so we don't round the corners on the bottom
        // edge since that looks kinda weird. Perhaps this logic could be replaced with some smart css?
        if (
          records[rowIndex + 1]?.row &&
          this.selectedRows[this.getKey(records[rowIndex + 1]?.row)]
        ) {
          return `${rowClass} selected bottom-straight`
        }
        return `${rowClass} selected`
      }
      return rowClass
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';
.btn {
  white-space: normal;
}

table.dataTable thead [class*='sorting'] {
  background-position: center right 0px;
}

table {
  border-collapse: collapse;
}
th {
  position: sticky;
  top: 0;
  min-width: 50px;
  background: white;
  z-index: 4;
}

.loading-overlay {
  top: 5px;
  left: 0;
  right: 0;
  bottom: 5px;
  z-index: 20;
  position: absolute;
  cursor: progress;
  overflow: hidden;
  opacity: 0.2;
  background-color: lightgray;
}

// Table sorting
.sort-enabled {
  @apply tw-select-none;
  @apply tw-cursor-pointer;
  &:hover,
  &.asc,
  &.desc {
    font-weight: 500;
    color: #0e0d12;
    svg {
      @apply tw-visible;
    }
  }

  &.desc {
    svg {
      @apply tw-rotate-180;
    }
  }
}

// Mixin for common sticky behavior
@mixin sticky-cell {
  position: sticky;
  right: 0;
  z-index: 2;

  &:focus-within {
    z-index: 3;
  }
}

// Apply the mixin for both table headers and cells,
// with overrides for individual differences.
th.sticky-enabled {
  @include sticky-cell;
  z-index: 13;
}

td.sticky-enabled {
  @include sticky-cell;
  background-color: white;
}

// Nesting the pseudo-element styles under .stickyShadow
.stickyShadow {
  .sticky-enabled {
    &::before {
      content: '';
      position: absolute;
      left: -10px; // adjust based on desired shadow width
      top: 0;
      bottom: -1px;
      width: 10px;
      pointer-events: none;
      box-shadow: inset -10px 0px 10px -8px rgba(0, 0, 0, 0.1);
    }
  }

  // Override the bottom value specifically for th
  th.sticky-enabled {
    &::before {
      bottom: -8px;
    }
  }
}

.sort-icon {
  @apply tw-inline !important;
  @apply tw-transition-transform;
}

// Table header
thead > tr > th {
  @apply tw-bg-true-gray-50;
  @apply tw-text-[13px];
  @apply tw-text-gray-600;
  @apply tw-border-b-0;

  &:first-child {
    @apply tw-rounded-l-lg;
  }

  &:last-child {
    @apply tw-rounded-r-lg;
  }
}

// Table rows
tbody > tr {
  &:first-child > td {
    border-top: 1px solid transparent;
  }
}

tbody > tr:not(.table-empty) {
  &:last-child > td {
    @apply tw-pb-2;
    border-bottom: 1px solid $gray-100;
  }

  &:not(:first-child) > td {
    border-top: 1px solid $gray-100;
  }

  > td:not(.skip-font-adjustments),
  td:not(.skip-font-adjustments) :deep(.wds-text) {
    @apply tw-align-middle;
    @apply tw-text-[13px];
    @apply tw-text-gray-850;
  }

  &.selected {
    &.row-hover:hover {
      > td {
        @apply tw-bg-indigo-50;
      }
    }

    > td {
      @apply tw-bg-[#F8F6FF];
      border-bottom: 1px solid transparent;
      border-top: 1px solid transparent;

      &:first-child {
        @apply tw-rounded-l-lg;
      }

      &:last-child {
        @apply tw-rounded-r-lg;
      }
    }

    + tr.selected {
      > td {
        &:first-child {
          @apply tw-rounded-tl-none;
        }

        &:last-child {
          @apply tw-rounded-tr-none;
        }
      }
    }
  }

  &.selected.bottom-straight {
    > td {
      &:first-child {
        @apply tw-rounded-bl-none;
      }

      &:last-child {
        @apply tw-rounded-br-none;
      }
    }
  }

  &.row-hover:hover:not(.selected) {
    > td {
      @apply tw-bg-true-gray-50;

      &:first-child {
        @apply tw-rounded-l-lg;
      }

      &:last-child {
        @apply tw-rounded-r-lg;
      }

      border-bottom: 1px solid transparent;
      border-top: 1px solid transparent;
    }
  }
}

.table-wrap {
  overflow: auto;
}

tbody {
  &:before {
    @apply tw-content-[''];
    @apply tw-block;
    @apply tw-h-[7px];
  }
}

.cols-header-move {
  transition: transform 500ms ease;
}

.cols-header-flip-locked .cols-header-move {
  transition: none;
  transform: none;
}

.locked-animation {
  transition: none;
}

// Ghost effect for the column being dragged
// Applied to both header and body cells to show the entire column as "being moved"
th.column-dragging,
td.column-dragging {
  opacity: 0.3;
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
